import { Row, Button, Skeleton, Card, Typography, Spin } from "antd";
import { StarOutlined, StarFilled, LoadingOutlined, PaperClipOutlined } from "@ant-design/icons";
import AlternativeLogo from "components/AlternativeLogo/AlternativeLogo";
import { CompanyLogo } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import RelevantStudyYearsDisplay from "components/RelevantStudyYearsDisplay/RelevantStudyYearsDisplay";
import { useHistory } from "react-router-dom";
import { JOB_TYPE_DESCRIPTIONS } from "constants/jobTypes";
import { ContactPerson, JobPosting } from "containers/SingleJobPosting/interfaces";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { isCompanyUser, useLoginContext } from "@bindeleddet/core/contexts/login";
import axios from "axios";
import { FavoriteJob } from "@bindeleddet/core/types";
import FollowCompanyButton from "components/FollowCompanyButton/FollowCompanyButton";
import { StyledText, StyledWrapper, StyledLogoCard } from "./style";

const { Text } = Typography;

export interface Props {
  companyId?: number;
  companyName?: string;
  imageLink?: string;
  job: JobPosting | undefined;
  contactPerson?: ContactPerson;
}

const JobDescription: React.FC<Props> = ({ companyId, companyName, imageLink, job, contactPerson }) => {
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [favoriteJobs, setFavoriteJobs] = useState<FavoriteJob[]>([]);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);

  const { currentUser } = useLoginContext();
  const history = useHistory();

  useEffect(() => {
    if (!currentUser || isCompanyUser(currentUser)) {
      setFavoritesLoaded(true);
      setFavoriteLoading(false);
      return;
    }
    setFavoriteLoading(true);
    axios
      .get<FavoriteJob[]>("/jobs/favorites/")
      .then(response => {
        setFavoriteJobs(response.data);
        setFavoritesLoaded(true);
        setFavoriteLoading(false);
      })
      .catch(() => {
        setFavoritesLoaded(true);
        setFavoriteLoading(false);
      });
  }, [currentUser]);

  const openInNewTab = (url: string) => {
    window.open(url, "_blank")?.focus();
  };

  const getAttachmentUrl = () => {
    if (!job?.attachment) return "";
    return job.attachment.includes("http") ? job.attachment : axios.defaults.baseURL + job.attachment;
  };

  const getAttachmentFileName = () => {
    if (!job?.attachment) return "";
    const pathParts = job.attachment.split("/");
    return pathParts[pathParts.length - 1] || "Vedlegg";
  };

  const createFavJob = () => {
    if (!currentUser || isCompanyUser(currentUser) || !job) return;
    setFavoriteLoading(true);
    axios
      .post<FavoriteJob>("/jobs/favorites/", {
        student: currentUser.id,
        job: job.id,
      })
      .then(response => {
        message(
          "success",
          "Stillingsannonse lagt til i favoritter",
          'Du kan se en oversikt over dine favoritter under "Min profil"'
        );
        setFavoriteJobs([...favoriteJobs, { id: response.data.id, student: currentUser.id, job: job.id }]);
        setFavoriteLoading(false);
      })
      .catch(() => {
        message("error", "Kunne ikke legge til stillingsannonse i favoritter");
        setFavoriteLoading(false);
      });
  };

  const delFavJob = () => {
    if (!job) return;
    setFavoriteLoading(true);
    const favId = favoriteJobs.find(fav => fav.job === job.id)?.id;
    axios
      .delete<FavoriteJob>(`/jobs/favorites/${favId}/`)
      .then(() => {
        message("success", "Stillingsannonse fjernet fra favoritter");
        setFavoriteJobs(favoriteJobs.filter(fav => fav.job !== job.id));
        setFavoriteLoading(false);
      })
      .catch(() => {
        message("error", "Kunne ikke fjerne stillingsannonse fra favoritter");
        setFavoriteLoading(false);
      });
  };

  const isFavorite = job ? favoriteJobs.some(fav => fav.job === job.id) : false;
  const showFavoriteButton = favoritesLoaded && currentUser && !isCompanyUser(currentUser);
  const showFollowButton = currentUser && !isCompanyUser(currentUser) && !!companyId;

  return (
    <>
      <Card bodyStyle={{ padding: "2em" }}>
        <Row justify="center" style={{ paddingBottom: "2em" }}>
          {imageLink && companyId ? (
            <StyledLogoCard bordered={false} onClick={() => history.push(`/companies/${companyId}`)}>
              {job?.alt_logo ? (
                <AlternativeLogo size="90%" src={job.alt_logo} />
              ) : (
                <CompanyLogo size="90%" companyId={companyId} src={imageLink} />
              )}
            </StyledLogoCard>
          ) : (
            <>
              <StyledWrapper>
                <Skeleton.Image />
              </StyledWrapper>
            </>
          )}
        </Row>
        <Row>
          <StyledText>
            <b>Stillingstype</b>
          </StyledText>
        </Row>
        <Row style={{ paddingBottom: "1em" }}>
          {(job?.job_type as string) ? (
            <StyledText>
              {JOB_TYPE_DESCRIPTIONS.find(type => type.value === job?.job_type)?.label || job?.job_type}
            </StyledText>
          ) : (
            <Skeleton.Button active style={{ height: "1.1em" }} />
          )}
        </Row>
        <Row>
          <StyledText>
            <b>Sted</b>
          </StyledText>
        </Row>
        <Row style={{ paddingBottom: "1em" }}>
          {job?.location ? (
            <StyledText>{job.location}</StyledText>
          ) : (
            <Skeleton.Button active style={{ height: "1.1em" }} />
          )}
        </Row>
        <Row>
          <StyledText>
            <b>Søknadsfrist</b>
          </StyledText>
        </Row>
        <Row style={{ paddingBottom: "1em" }}>
          {job?.deadline ? (
            <StyledText>{dayjs(job.deadline).format("DD.MM.YYYY")}</StyledText>
          ) : (
            <Skeleton.Button active style={{ height: "1.1em" }} />
          )}
        </Row>
        <Row>
          <StyledText>
            <b>Årstrinn</b>
          </StyledText>
        </Row>
        <Row style={{ paddingBottom: "1em" }}>
          {job?.year_levels ? (
            <RelevantStudyYearsDisplay yearLevelsAccepted={job.year_levels} />
          ) : (
            <Skeleton.Button active style={{ height: "1.1em" }} />
          )}
        </Row>
        <Row>
          <StyledText>
            <b>Kontaktperson</b>
          </StyledText>
        </Row>

        <Row style={{ paddingBottom: "0.6em" }}>
          {contactPerson ? (
            <StyledText>
              <a href={`mailto:${contactPerson.email}`}>{`${contactPerson.first_name} ${contactPerson.last_name}`}</a>
            </StyledText>
          ) : (
            <Text italic>Ikke bestemt</Text>
          )}
        </Row>
        {job?.attachment && (
          <>
            <Row>
              <StyledText>
                <b>Vedlegg</b>
              </StyledText>
            </Row>
            <Row style={{ paddingBottom: "0.6em", paddingTop: "0.6em" }}>
              <a
                href={getAttachmentUrl()}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: "100%",
                  padding: "5px",
                  border: "1px solid var(--neutral-alter)",
                  borderRadius: "6px",
                  cursor: "pointer",
                  backgroundColor: "var(--neutral-alt)",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = "var(--primary-color)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = "var(--neutral-alter)";
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <PaperClipOutlined style={{ fontSize: "16px" }} />
                  <span>{getAttachmentFileName()}</span>
                </div>
              </a>
            </Row>
          </>
        )}
        {job?.search_url && (
          <Row justify="center" style={{ paddingBottom: "0.6em", paddingTop: "0.9em" }}>
            <Button
              size="large"
              type="primary"
              style={{ background: "@primary-color", border: "none", width: "100%" }}
              onClick={() => {
                openInNewTab(job.search_url);
              }}
            >
              Søk på stillingen
            </Button>
          </Row>
        )}
        {showFavoriteButton && (
          <Row justify="center" style={{ paddingTop: "0.6em" }}>
            <Button
              size="large"
              type="default"
              icon={
                favoriteLoading ? (
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 16, color: "#f3922d", marginRight: 8 }} spin />}
                  />
                ) : isFavorite ? (
                  <StarFilled style={{ color: "#f3922d", fontSize: 16 }} />
                ) : (
                  <StarOutlined style={{ color: "#f3922d", fontSize: 16 }} />
                )
              }
              disabled={favoriteLoading}
              onClick={isFavorite ? delFavJob : createFavJob}
              style={{ width: "100%" }}
            >
              {isFavorite ? "Fjern fra favoritter" : "Legg til i favoritter"}
            </Button>
          </Row>
        )}
        {showFollowButton && (
          <Row justify="center" style={{ paddingTop: "0.6em" }}>
            <FollowCompanyButton companyId={companyId} companyName={companyName} displayCompanyName />
          </Row>
        )}
      </Card>
    </>
  );
};

export default JobDescription;

// harkodet fargekode: #59C09C
